import { ApiError } from '@api/api-client';
import {
  type ImportPmsVoterRow,
  importPmsVoters,
  type ImportPmsVotersResult,
} from '@api/pms-voters-api';
import Modal from '@components/modal';
import {
  buildFailedPmsRowsCsv,
  parsePmsVoterRows,
  validatePmsVoterHeaders,
} from '@utils/csv-pms-parser';
import { downloadCsv } from '@utils/csv-utils';
import { AlertTriangle, CheckCircle, Download, FileUp, Upload, X } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { sileo } from 'sileo';

/** The minimum an edition has to say about itself to be picked as a target. */
export interface ImportPmsVotersEdition {
  id: string;
  name: string;
  siteCode: string;
}

interface ImportPmsVotersModalProps {
  isOpen: boolean;
  /** The password-mode editions an import can feed; the target is picked here. */
  editions: ImportPmsVotersEdition[];
  /** Pre-selection, when the page filter already names an edition. */
  defaultEditionId?: string;
  onClose: () => void;
  onImported: () => void;
}

type ImportState = 'idle' | 'preview' | 'importing' | 'done';

/**
 * Voter census import. Nominees — of a PMS site or a marketplace one — go through
 * the edition importer in Editions → Import CSV, which owns editions, categories
 * and nominations for every site.
 */
export default function ImportPmsVotersModal({
  isOpen,
  editions,
  defaultEditionId = '',
  onClose,
  onImported,
}: ImportPmsVotersModalProps): React.JSX.Element {
  const [editionId, setEditionId] = useState(defaultEditionId);
  const [state, setState] = useState<ImportState>('idle');
  const [fileName, setFileName] = useState('');
  const [voterRows, setVoterRows] = useState<ImportPmsVoterRow[]>([]);
  const [result, setResult] = useState<ImportPmsVotersResult | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const rowCount = voterRows.length;
  const selectedEdition = editions.find((edition) => edition.id === editionId);

  const reset = useCallback((): void => {
    setState('idle');
    setFileName('');
    setVoterRows([]);
    setResult(null);
    setIsDragging(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleClose = (): void => {
    reset();
    onClose();
  };

  const processFile = useCallback((file: File): void => {
    if (!/\.(csv|tsv|txt)$/i.test(file.name)) {
      sileo.error({ title: 'Invalid file type. Please upload a CSV/TSV file.' });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;

      const validationError = validatePmsVoterHeaders(content);
      if (validationError) {
        sileo.error({ title: validationError });
        return;
      }

      const parsed = parsePmsVoterRows(content);
      if (parsed.length === 0) {
        sileo.error({ title: 'No valid data rows found in the file.' });
        return;
      }

      setVoterRows(parsed);
      setFileName(file.name);
      setState('preview');
    };
    reader.readAsText(file);
  }, []);

  const handleImport = async (): Promise<void> => {
    if (!editionId) return;
    setState('importing');
    try {
      const imported = await importPmsVoters(editionId, voterRows);

      setResult(imported);
      setState('done');
      onImported();

      if (imported.errors.length === 0) {
        sileo.success({
          title: `Import complete: ${imported.created} created, ${imported.updated} updated`,
        });
      } else {
        sileo.warning({ title: `Import finished with ${imported.errors.length} warning(s)` });
      }
    } catch (error) {
      sileo.error({ title: error instanceof ApiError ? error.message : 'Import failed' });
      setState('preview');
    }
  };

  /**
   * The target edition. It used to be the page filter, which made the list below
   * decide what an import wrote to; here it sits next to the file it applies to.
   */
  const renderEditionField = (): React.JSX.Element => (
    <div className="form-group">
      <label htmlFor="import-pms-edition" className="form-label">
        Edition
      </label>
      <select
        id="import-pms-edition"
        className="form-select"
        value={editionId}
        disabled={editions.length === 0}
        onChange={(e) => setEditionId(e.target.value)}
      >
        <option value="">Select an edition...</option>
        {editions.map((edition) => (
          <option key={edition.id} value={edition.id}>
            {edition.name} &middot; {edition.siteCode}
          </option>
        ))}
      </select>
      <p className="form-hint">
        {editions.length === 0
          ? 'No password-mode edition exists yet. Create one under Editions first.'
          : 'The census this file feeds. Every voter in it is issued a token for this site.'}
      </p>
    </div>
  );

  const renderIdle = (): React.JSX.Element => (
    <div
      className={`csv-drop-zone${isDragging ? ' csv-drop-zone--active' : ''}${
        editionId ? '' : ' csv-drop-zone--disabled'
      }`}
      onDragOver={(e) => {
        e.preventDefault();
        if (editionId) setIsDragging(true);
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        setIsDragging(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragging(false);
        if (!editionId) return;
        const file = e.dataTransfer.files[0];
        if (file) processFile(file);
      }}
      onClick={() => {
        if (editionId) fileInputRef.current?.click();
      }}
      role="button"
      aria-disabled={!editionId}
      tabIndex={editionId ? 0 : -1}
      onKeyDown={(e) => {
        if (!editionId) return;
        if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click();
      }}
    >
      <Upload size={40} className="csv-drop-zone__icon" />
      <p className="csv-drop-zone__title">
        {editionId ? 'Drag & drop your CSV file here' : 'Pick an edition to enable the upload'}
      </p>
      <p className="csv-drop-zone__subtitle">or click to browse. Supported: .csv, .tsv, .txt</p>
      <p className="csv-drop-zone__hint">
        Import a HubSpot contact list; each new voter is issued a token to export back
      </p>
      <p className="csv-drop-zone__hint">
        Columns: Record ID - Contact, Email (Awards Voting Token is filled by the export)
      </p>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.tsv,.txt"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) processFile(file);
        }}
        style={{ display: 'none' }}
      />
    </div>
  );

  const renderPreview = (): React.JSX.Element => (
    <div className="csv-preview">
      <div className="csv-file-info">
        <FileUp size={20} className="csv-file-info__icon" />
        <div className="csv-file-info__details">
          <p className="csv-file-info__name">{fileName}</p>
          <p className="csv-file-info__meta">
            {rowCount} voter{rowCount !== 1 ? 's' : ''} &middot; target site{' '}
            <strong>{selectedEdition?.siteCode ?? '—'}</strong>
          </p>
        </div>
        <button
          type="button"
          className="btn btn-ghost btn-icon btn-sm"
          onClick={reset}
          aria-label="Remove file"
        >
          <X size={16} />
        </button>
      </div>

      <div className="csv-preview__table-wrapper">
        <table className="csv-preview__table">
          <thead>
            <tr>
              <th>Record ID (HubSpot)</th>
              <th>Email</th>
            </tr>
          </thead>
          <tbody>
            {voterRows.slice(0, 20).map((row) => (
              <tr key={`${row.hsid}-${row.email}`}>
                <td>{row.hsid || '—'}</td>
                <td>{row.email}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rowCount > 20 && (
          <p className="csv-file-info__meta">Showing the first 20 of {rowCount} rows.</p>
        )}
      </div>
    </div>
  );

  const renderDone = (): React.JSX.Element | null => {
    if (!result) return null;

    const hasErrors = result.errors.length > 0;

    return (
      <div className="csv-result">
        <div
          className={`csv-result__banner csv-result__banner--${hasErrors ? 'warning' : 'success'}`}
        >
          {hasErrors ? (
            <AlertTriangle size={24} className="csv-result__banner-icon--warning" />
          ) : (
            <CheckCircle size={24} className="csv-result__banner-icon--success" />
          )}
          <p className="csv-result__banner-title">
            {hasErrors ? 'Import completed with warnings' : 'Import completed successfully'}
          </p>
        </div>

        <div className="csv-result__stats">
          <div className="csv-result__stat">
            <p className="csv-result__stat-value csv-result__stat-value--primary">
              {result.created}
            </p>
            <p className="csv-result__stat-label">Created</p>
          </div>
          <div className="csv-result__stat">
            <p className="csv-result__stat-value csv-result__stat-value--info">{result.updated}</p>
            <p className="csv-result__stat-label">Updated</p>
          </div>
          <div className="csv-result__stat">
            <p className="csv-result__stat-value csv-result__stat-value--muted">{result.skipped}</p>
            <p className="csv-result__stat-label">Skipped</p>
          </div>
        </div>

        {result.hsidOverlapWarning && (
          <div className="csv-result__warnings" role="alert">
            <p className="csv-result__warnings-title">Check the HubSpot ID column</p>
            <p>{result.hsidOverlapWarning}</p>
          </div>
        )}

        {hasErrors && (
          <div className="csv-result__warnings">
            <p className="csv-result__warnings-title">Warnings:</p>
            <ul className="csv-result__warnings-list">
              {result.errors.map((err) => (
                <li key={err}>{err}</li>
              ))}
            </ul>
          </div>
        )}

        {result.failedRows.length > 0 && (
          <button
            type="button"
            className="btn btn-secondary"
            style={{ alignSelf: 'flex-start' }}
            onClick={() =>
              downloadCsv(
                buildFailedPmsRowsCsv(
                  result.failedRows.map((failed) => ({
                    row: { ...failed.row } as Record<string, unknown>,
                    reason: failed.reason,
                  })),
                ),
                'failed-pms-voter-rows.csv',
              )
            }
          >
            <Download size={16} />
            Download failed rows ({result.failedRows.length})
          </button>
        )}
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        state === 'done'
          ? 'Import results'
          : state === 'preview'
            ? 'Review import data'
            : 'Import PMS voters from CSV'
      }
      footer={
        state === 'done' ? (
          <button type="button" className="btn btn-primary" onClick={handleClose}>
            Close
          </button>
        ) : state === 'preview' ? (
          <>
            <button type="button" className="btn btn-secondary" onClick={reset}>
              Back
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!editionId}
              data-tooltip={!editionId ? 'Pick an edition first' : undefined}
              onClick={() => void handleImport()}
            >
              <Upload size={16} />
              Import {rowCount} voter{rowCount !== 1 ? 's' : ''}
            </button>
          </>
        ) : (
          <button type="button" className="btn btn-secondary" onClick={handleClose}>
            Cancel
          </button>
        )
      }
    >
      {(state === 'idle' || state === 'preview') && (
        <div className="modal-form">
          {renderEditionField()}
          {state === 'idle' ? renderIdle() : renderPreview()}
        </div>
      )}
      {state === 'importing' && (
        <div className="csv-importing">
          <p className="csv-importing__title">Importing...</p>
          <p className="csv-importing__subtitle">Processing {rowCount} rows</p>
        </div>
      )}
      {state === 'done' && renderDone()}
    </Modal>
  );
}
